import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  SelectChangeEvent,
  Switch,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ConfirmModal } from "../../../components/ConfirmModal/ConfirmModal";
import { DataTable, ColumnDef } from "../../../components/DataTable/DataTable";
import { NAVBAR_HEIGHT } from "../../../components/Navbar/NavDesktop";
import { useLanguages } from "../../../context/LanguagesContext";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { usePortal } from "../../../context/PortalsContext";
import { useToast } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";
import {
  addUpdateUserAttribute,
  getUserAttributes,
  reorderAttributes,
} from "../../../services/api/apiClient";
import {
  AttributesResponse,
  GetUserAttributesParams,
  GetUserAttributesParamsByID,
  UserAttribute,
} from "../../../services/api/types/portalConfiguration/userAttribute";
import { brand, gray } from "../../../theme/themePrimitives";
import { AddNewAttributeModal } from "./components/AddNewAttributeModal";
import {
  StatusFilterDrawer,
  StatusFilterKey,
  StatusFilterState,
} from "./components/StatusFilterDrawer";
import { extractErrorMessage } from "../../../utils/errorUtils";

interface UserAttributesProps {}

export const UserAttributes: React.FC<UserAttributesProps> = () => {
  const [attributesResponse, setAttributesResponse] =
    useState<AttributesResponse | null>(null);
  const [attributes, setAttributes] = useState<UserAttribute[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [attribute, setAttribute] = useState("");
  const [attributeKey, setAttributeKey] = useState("");
  const [dataType, setDataType] = useState("string");
  const [mandatory, setMandatory] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isLineManager, setIsLineManager] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  const [attributeError, setAttributeError] = useState("");
  const [attributeKeyError, setAttributeKeyError] = useState("");
  const [dataTypeError, setDataTypeError] = useState("");
  const [mandatoryError, setMandatoryError] = useState("");
  const [isLineManagerError, setIsLineManagerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState<{
    uid: string | null;
    field: string | null;
  }>({
    uid: null,
    field: null,
  });
  const { showToast } = useToast();
  const [selectedAttribute, setSelectedAttribute] = useState<UserAttribute>();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { portalUid } = useParams();
  const { auth } = useAuth();
  const { languages, dispatch } = useLanguages();
  const { portals } = usePortal();
  const [currentAttributeData, setCurrentAttributeData] =
    useState<GetUserAttributesParamsByID>({
      portalId: portalUid,
      userAttributeId: "",
      lang: languages.selectedLanguage,
    });

  useEffect(() => {
    setCurrentAttributeData((prev) => ({
      ...prev,
      portalId: portalUid ?? undefined,
      lang: languages.selectedLanguage,
    }));
  }, [portalUid, languages.selectedLanguage]);

  const defaultStatusFilter: StatusFilterState = useMemo(
    () => ({
      active: true,
      inactive: false,
    }),
    [],
  );

  const [showFilter, setShowFilter] = useState(false);
  const [statusFilterDraft, setStatusFilterDraft] =
    useState<StatusFilterState>(defaultStatusFilter);
  const [appliedStatusFilter, setAppliedStatusFilter] =
    useState<StatusFilterState>(defaultStatusFilter);
  const [hasSelectedFilter, setHasSelectedFilter] = useState(false);

  const getIsActiveFilterValue = useCallback(
    (filter: StatusFilterState): boolean | undefined => {
      if (filter.active && !filter.inactive) {
        return true;
      }
      if (!filter.active && filter.inactive) {
        return false;
      }
      return undefined;
    },
    [],
  );
  const clearFilters = () => {
    const clearedFilter = { active: false, inactive: false };
    setStatusFilterDraft(clearedFilter);
    setAppliedStatusFilter(clearedFilter);
    setPage(1);
    setShowFilter(false);
  };

  useEffect(() => {
    if (!showFilter) {
      setStatusFilterDraft(appliedStatusFilter);
    }
  }, [showFilter, appliedStatusFilter]);

  const filteredAttributes = useMemo(() => {
    const { active, inactive } = appliedStatusFilter;

    if (!active && !inactive) {
      return attributes;
    }

    return attributes.filter((attr) => {
      if (active && attr.isActive) return true;
      if (inactive && !attr.isActive) return true;
      return false;
    });
  }, [appliedStatusFilter, attributes]);

  const handleChipDelete = (key: StatusFilterKey) => {
    const updatedFilter = {
      ...appliedStatusFilter,
      [key]: !appliedStatusFilter[key],
    };

    setAppliedStatusFilter(updatedFilter);
    setPage(1);
  };

  const renderChips = () => {
    const chips = [];

    if (appliedStatusFilter.active) {
      chips.push(
        <Chip
          key="active"
          label="Status: Active"
          onDelete={() => handleChipDelete("active")}
          sx={{
            backgroundColor: brand[600],
            border: `1px solid ${gray[300]}`,
            padding: "16px 8px",
            borderRadius: "6px",
            "& .MuiChip-label": {
              color: "white",
            },
          }}
        />,
      );
    }

    if (appliedStatusFilter.inactive) {
      chips.push(
        <Chip
          key="inactive"
          label="Status: Inactive"
          onDelete={() => handleChipDelete("inactive")}
          sx={{
            backgroundColor: brand[600],
            border: `1px solid ${gray[300]}`,
            padding: "16px 8px",
            borderRadius: "6px",
            "& .MuiChip-label": {
              color: "white",
            },
          }}
        />,
      );
    }

    return chips;
  };

  const handleAttributeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setAttributeError("");
    } else {
      setAttributeError("Attribute is required.");
    }
    setAttribute(e.target.value);
  };

  const handleAttributeKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setAttributeKeyError("");
    } else {
      setAttributeKeyError("Attribute Key is required.");
    }
    setAttributeKey(e.target.value);
  };

  const handleDataTypeChange = (e: SelectChangeEvent<string>) => {
    setDataType(e.target.value);
    setDataTypeError("");
  };

  const handleMandatoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMandatory(e.target.checked);
    setMandatoryError("");
  };

  const handleIsAvailableChange = (e: any) => {
    const nextIsAvailable = e.target.checked;

    setIsAvailable(nextIsAvailable);
    setIsEditable(false);
  };

  const handleIsLineManagerChange = (e: any) => {
    setIsLineManager(e.target.checked);
    setIsLineManagerError("");
  };

  const handleRowsChange = (rows: number) => {
    setLimit(rows);
    setPage(1);
  };

  const toggleStatusFilterDraft = (key: StatusFilterKey) => {
    setStatusFilterDraft((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveFilters = () => {
    setAppliedStatusFilter(statusFilterDraft);
    setPage(1);
    setShowFilter(false);
  };

  const confirmModalButtonClick = () => {
    setMandatory((prev) => !prev);
    setOpenConfirmModal(false);
  };

  const fetchAttributes = useCallback(
    async (
      params: GetUserAttributesParams = {
        languageCode: languages.selectedLanguage,
      },
    ) => {
      try {
        setLoading(true);
        if (!portals.selectedPortal) {
          return;
        }
        const response: AttributesResponse = await getUserAttributes(
          portals.selectedPortal.portalUid,
          {
            ...params,
            isActive:
              params.isActive ?? getIsActiveFilterValue(appliedStatusFilter),
          },
        );
        setAttributesResponse(response);
        setAttributes(response.rows);
      } catch (error: any) {
        console.log("Error:", error);

        setAttributes([]);
        setAttributesResponse(null);
        if (error.response.data === "No user attributes found!") return;
        showToast(
          extractErrorMessage(error, "Failed to fetch user attributes"),
          "error",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      appliedStatusFilter,
      getIsActiveFilterValue,
      languages.selectedLanguage,
      portals.selectedPortal,
      showToast,
    ],
  );

  useEffect(() => {
    if (portals.selectedPortal) {
      dispatch({ type: "SET_SELECTED_LANGUAGE", payload: "en" });
    }
  }, [dispatch, portals.selectedPortal]);

  useEffect(() => {
    if (portals.selectedPortal && languages.selectedLanguage) {
      setPage(1);
      fetchAttributes({
        languageCode: languages.selectedLanguage,
        limit: limit,
      });
    }
  }, [
    appliedStatusFilter,
    portals.selectedPortal,
    languages.selectedLanguage,
    limit,
    fetchAttributes,
  ]);

  useEffect(() => {
    const isFilterApplied =
      appliedStatusFilter.active || appliedStatusFilter.inactive;
    setHasSelectedFilter(isFilterApplied);
  }, [appliedStatusFilter]);

  const handlePageChange = (newPage: number) => {
    fetchAttributes({
      limit: limit,
      offset: (newPage - 1) * limit,
      languageCode: languages.selectedLanguage,
    });
    setPage(newPage);
  };

  const handleEditClick = useCallback(
    (
      attributeObject: UserAttribute,
      attribute: string,
      attributeKey: string,
      mandatory: boolean,
      dataType: string,
      isAvailable: boolean,
      isActive: boolean,
      isLineManager: boolean,
      isEditable?: boolean,
    ) => {
      setSelectedAttribute(attributeObject);
      setAttribute(attribute);
      setAttributeKey(attributeKey);
      setMandatory(mandatory);
      setDataType(dataType);
      setIsAvailable(isAvailable);
      setIsLineManager(isLineManager);
      setIsEditable(isEditable ?? false);
      setIsEditing(true);
      setOpenModal(true);
      setCurrentAttributeData((prev) => ({
        ...prev,
        userAttributeId: attributeObject.portalUserAttributeUid,
      }));
      setAttributeKeyError("");
    },
    [],
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = filteredAttributes.findIndex(
      (item) => item.portalUserAttributeUid === active.id,
    );
    const newIndex = filteredAttributes.findIndex(
      (item) => item.portalUserAttributeUid === over.id,
    );

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newAttributes = arrayMove(filteredAttributes, oldIndex, newIndex);

    const updated = newAttributes.map((attr: UserAttribute, i: number) => ({
      ...attr,
      attributeOrder: i + 1,
    }));

    setAttributes(updated);
    const oldAttributeOrder = filteredAttributes[oldIndex]?.attributeOrder;
    const newAttributeOrder = filteredAttributes[newIndex]?.attributeOrder;

    try {
      if (portalUid && auth.user?.UserPortalUid) {
        await reorderAttributes(portalUid, active.id as string, {
          adminUser: auth.user?.UserPortalUid,
          attributeOrder: oldAttributeOrder,
          attributeOrderNew: newAttributeOrder,
        });
      }
    } catch (error: any) {
      console.log("Error", error);
      showToast(
        "There was an inconsistency with the database records.",
        "error",
      );
      await fetchAttributes();
    }
  };

  const toggleSwitch = useCallback(
    async (attribute: UserAttribute, field: string) => {
      try {
        if (portalUid && languages.selectedLanguage && auth.user) {
          setSwitchLoading({ uid: attribute.portalUserAttributeUid, field });

          const isLineManagerField = field === "lineManager";
          const isActiveField = field === "active";

          const updatedData = isLineManagerField
            ? { isLineManager: !attribute.isLineManager }
            : isActiveField
              ? { isActive: !attribute.isActive }
              : { isRequired: !attribute.isRequired };

          await addUpdateUserAttribute(portalUid, {
            adminUser: auth.user.UserPortalUid,
            languageCode: languages.selectedLanguage,
            attributeKey: attribute.attributeKey,
            ...updatedData,
          });
          setAttributes((prev) =>
            prev.map((attr) =>
              attr.portalUserAttributeUid === attribute.portalUserAttributeUid
                ? {
                    ...attribute,
                    ...(isLineManagerField
                      ? { isLineManager: !attribute.isLineManager }
                      : isActiveField
                        ? { isActive: !attribute.isActive }
                        : { isRequired: !attribute.isRequired }),
                  }
                : attr,
            ),
          );
        }
      } catch (error) {
        console.log("Error: ", error);
      } finally {
        setSwitchLoading({ uid: null, field: null });
      }
    },
    [portalUid, languages.selectedLanguage, auth.user],
  );

  const validateAttributeForm = (): boolean => {
    const newAttributeError = !attribute.trim() ? "Attribute is required." : "";
    const newAttributeKeyError = !attributeKey.trim()
      ? "Attribute key is required."
      : "";
    const newDataTypeError = !dataType.trim() ? "Data type is required." : "";

    setAttributeError(newAttributeError);
    setAttributeKeyError(newAttributeKeyError);
    setDataTypeError(newDataTypeError);

    return !newAttributeError && !newAttributeKeyError && !newDataTypeError;
  };

  const checkDuplicateAttribute = async (
    name: string,
    key: string,
  ): Promise<boolean> => {
    if (!portalUid) return false;

    try {
      const response: AttributesResponse = await getUserAttributes(
        portalUid,
        {
          languageCode: languages.selectedLanguage,
          short: true,
        },
      );

      const normalizedName = name.trim().toLowerCase();
      const normalizedKey = key.trim().toLowerCase();

      const nameExists = response.rows.some(
        (attr) => attr.attributeName.trim().toLowerCase() === normalizedName,
      );
      const keyExists = response.rows.some(
        (attr) => attr.attributeKey.trim().toLowerCase() === normalizedKey,
      );

      if (nameExists) {
        setAttributeError("An attribute with this name already exists.");
      }
      if (keyExists) {
        setAttributeKeyError("An attribute with this key already exists.");
      }

      return nameExists || keyExists;
    } catch (error: any) {
      console.log("Error:", error);
      return false;
    }
  };

  const addNewAttribute = async () => {
    try {
      if (!validateAttributeForm()) return;

      setButtonLoading(true);

      if (portalUid) {
        let formattedKey = attributeKey.replace(/\s+/g, "_");

        if (isEditing && selectedAttribute) {
          formattedKey = selectedAttribute.attributeKey;
        }

        if (!isEditing) {
          const isDuplicate = await checkDuplicateAttribute(
            attribute,
            formattedKey,
          );
          if (isDuplicate) {
            setButtonLoading(false);
            return;
          }
        }

        const newAttributeOrder = isEditing
          ? selectedAttribute?.attributeOrder
          : (attributesResponse?.count ?? 0) + 1;
        const attributeIsActive = isEditing
          ? (selectedAttribute?.isActive ?? true)
          : true;

        const requestBody: any = {
          languageCode: languages.selectedLanguage,
          attributeName: attribute,
          attributeKey: formattedKey,
          dataType: dataType,
          isRequired: mandatory,
          isActive: attributeIsActive,
          isAvailable: isAvailable,
          isLineManager: isLineManager,
          isEditable: isEditable,
          adminUser: auth.user?.UserPortalUid!,
        };

        if (!isEditing) {
          requestBody.attributeOrder = newAttributeOrder;
        }

        const response = await addUpdateUserAttribute(portalUid, requestBody);

        const updatedAttribute: UserAttribute = {
          portalUserAttributeUid: response.userAttribute,
          attributeName: attribute,
          attributeKey: formattedKey,
          dataType: dataType,
          isRequired: mandatory,
          isActive: attributeIsActive,
          isAvailable: isAvailable,
          isLineManager: isLineManager,
          isEditable: isEditable,
          attributeOrder:
            isEditing && selectedAttribute
              ? selectedAttribute.attributeOrder
              : newAttributeOrder!,
        };

        if (isEditing && selectedAttribute) {
          setAttributes((prev) =>
            prev.map((attr) =>
              attr.portalUserAttributeUid ===
              selectedAttribute.portalUserAttributeUid
                ? updatedAttribute
                : attr,
            ),
          );
        } else {
          const currentCount = attributesResponse?.count ?? 0;
          const totalPages = Math.ceil((currentCount + 1) / limit);
          const pageNumber = totalPages;
          const offset = (pageNumber - 1) * limit;

          await fetchAttributes({
            limit,
            offset,
            languageCode: languages.selectedLanguage,
          });
          setPage(pageNumber);
        }
        setAttribute("");
        setAttributeKey("");
        setDataType("");
        setMandatory(false);
        setIsAvailable(false);
        setIsLineManager(false);
        setIsEditable(false);
        setOpenModal(false);
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(extractErrorMessage(error, "Something went wrong."), "error");
    } finally {
      setButtonLoading(false);
    }
  };

  usePageMetadata(
    {
      title: "User attributes",
      breadcrumbs: [
        {
          label: "Portal configuration",
          path: `/portal-configuration`,
        },
        {
          label: "User attributes",
        },
      ],
    },
    [],
  );

  const columns: ColumnDef<UserAttribute>[] = useMemo(
    () => [
      {
        id: "attributeName",
        label: "Attribute",
        render: (row) => (
          <Typography
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(
                row,
                row.attributeName,
                row.attributeKey,
                row.isRequired,
                row.dataType,
                row.isAvailable,
                row.isActive,
                row.isLineManager,
                row.isEditable,
              );
            }}
            sx={{
              fontWeight: 600,
              color: brand[600],
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {row.attributeName}
          </Typography>
        ),
      },
      {
        id: "attributeKey",
        label: "Attribute Key",
      },
      {
        id: "dataType",
        label: "Data Type",
      },
      {
        id: "isRequired",
        label: "Mandatory",
        render: (row) => (
          <>
            {switchLoading.uid === row.portalUserAttributeUid &&
            switchLoading.field === "mandatory" ? (
              <CircularProgress size={20} />
            ) : (
              <Switch
                checked={row.isRequired}
                onClick={() => toggleSwitch(row, "mandatory")}
              />
            )}
          </>
        ),
      },
      {
        id: "isActive",
        label: "Status",
        render: (row) => (
          <>
            {switchLoading.uid === row.portalUserAttributeUid &&
            switchLoading.field === "active" ? (
              <CircularProgress size={20} />
            ) : (
              <Switch
                checked={row.isActive}
                onClick={() => toggleSwitch(row, "active")}
              />
            )}
          </>
        ),
      },
    ],
    [switchLoading, toggleSwitch, handleEditClick],
  );

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: `calc(100vh - ${NAVBAR_HEIGHT})`,
          }}
        >
          <CircularProgress size={100} />
        </Box>
      ) : (
        <>
          {attributesResponse && attributes && (
            <DataTable
              data={filteredAttributes}
              columns={columns}
              totalCount={attributesResponse.count}
              page={page}
              rowsPerPage={limit}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsChange}
              isLoading={loading}
              getRowId={(row) => row.portalUserAttributeUid}
              enableDragAndDrop={true}
              onDragEnd={handleDragEnd}
              title={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6">Attributes added</Typography>
                  <Box
                    width={24}
                    height={24}
                    borderRadius="50%"
                    bgcolor="#EFF8FF"
                    border={"1px solid #B2DDFF"}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Typography color="#175CD3">
                      {filteredAttributes.length}
                    </Typography>
                  </Box>
                </Box>
              }
              actions={
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "flex-end", width: "fit-content" }}
                >
                  <Button
                    onClick={() => {
                      setIsEditing(false);
                      setAttribute("");
                      setAttributeKey("");
                      setDataType("string");
                      setMandatory(false);
                      setIsAvailable(false);
                      setIsLineManager(false);
                      setIsEditable(false);
                      setAttributeError("");
                      setAttributeKeyError("");
                      setDataTypeError("");
                      setMandatoryError("");
                      setIsLineManagerError("");
                      setOpenModal(true);
                    }}
                    sx={{
                      px: 2,
                      borderRadius: "12px",
                      backgroundColor: `${brand[600]} !important`,
                      color: `white`,
                      "&:hover": {
                        bgcolor: `${brand[700]} !important`,
                      },
                    }}
                  >
                    Add New Attribute
                  </Button>
                  <Button
                    id="user-attributes-status-filter-button"
                    startIcon={<FilterListIcon sx={{ color: "black" }} />}
                    onClick={() => setShowFilter((prev) => !prev)}
                    sx={{
                      px: 2,
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid #D0D5DD",
                    }}
                  >
                    Filter
                  </Button>
                </Stack>
              }
              renderEmptyState={
                <Typography variant="body2" color="text.secondary">
                  No attributes found.
                </Typography>
              }
              headerContent={
                hasSelectedFilter ? (
                  <Collapse in={hasSelectedFilter} timeout="auto" unmountOnExit>
                    <Divider sx={{ pt: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pt: 2,
                        pb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                        }}
                      >
                        <Typography
                          sx={{ whiteSpace: "nowrap" }}
                          fontWeight={"bold"}
                        >
                          {`Filter Applied:`}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0.5rem",
                          }}
                        >
                          {renderChips()}
                        </Box>
                      </Box>

                      <Button
                        onClick={() => clearFilters()}
                        sx={{
                          color: brand[600],
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Clear All
                      </Button>
                    </Box>
                  </Collapse>
                ) : null
              }
            />
          )}
          <AddNewAttributeModal
            addNewAttribute={addNewAttribute}
            order={
              attributesResponse?.count
                ? isEditing
                  ? selectedAttribute?.attributeOrder!
                  : attributesResponse?.count! + 1
                : 1
            }
            attribute={attribute}
            attributeError={attributeError}
            attributeKey={attributeKey}
            attributeKeyError={attributeKeyError}
            setAttributeKeyError={setAttributeKeyError}
            handleAttributeKeyChange={handleAttributeKeyChange}
            dataType={dataType}
            dataTypeError={dataTypeError}
            handleDataTypeChange={handleDataTypeChange}
            mandatory={mandatory}
            mandatoryError={mandatoryError}
            handleMandatoryChange={handleMandatoryChange}
            handleAttributeChange={handleAttributeChange}
            openModal={openModal}
            setOpenModal={setOpenModal}
            setOpenConfirmModal={setOpenConfirmModal}
            isEditing={isEditing}
            isAvailable={isAvailable}
            handleIsAvailableChange={handleIsAvailableChange}
            setAttributeKey={setAttributeKey}
            buttonLoading={buttonLoading}
            isLineManager={isLineManager}
            handleIsLineManager={handleIsLineManagerChange}
            isLineManagerError={isLineManagerError}
            isEditable={isEditable}
            setIsEditable={setIsEditable}
            currentAttributeData={currentAttributeData}
          />
          <StatusFilterDrawer
            open={showFilter}
            onClose={() => setShowFilter(false)}
            filters={statusFilterDraft}
            onToggle={toggleStatusFilterDraft}
            onSave={handleSaveFilters}
            clearFilters={clearFilters}
          />
          <ConfirmModal
            openConfirmModal={openConfirmModal}
            setOpenConfirmModal={setOpenConfirmModal}
            confirmButtonClick={confirmModalButtonClick}
            modalTitle="Mandatory"
            modalSubtitle="Are you sure you want to turn it on/off?"
          />
        </>
      )}
    </>
  );
};
